import React, {
    forwardRef,
    RefObject,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import useResizeObserver from '@react-hook/resize-observer'
import { Size, SizeContext } from 'ui/hooks/useSizeContext'

import { Box, BoxProps } from './Box'

/**
 * Box providing size context
 */
export const SizeBox = forwardRef<HTMLDivElement, BoxProps>(({ children, ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => innerRef.current!)
    const size = useSize(innerRef as RefObject<HTMLDivElement>)

    const [isDeferred, setIsDeferred] = useState(false)

    useEffect(() => {
        setIsDeferred(true)
    }, [])

    return (
        <SizeContext.Provider value={{ size }}>
            <Box {...props} ref={innerRef}>
                {isDeferred && children}
            </Box>
        </SizeContext.Provider>
    )
})

const useSize = (target: RefObject<HTMLDivElement>) => {
    const [size, setSize] = useState<Size>({
        width: undefined,
        height: undefined,
    })

    const applySize = useCallback((el: HTMLDivElement | null) => {
        if (el) {
            const bounds = el.getBoundingClientRect()

            // not necessary, but can become handy - the current use case is to
            // grab height for a fixed container
            el.style.setProperty('--sizebox-width', `${bounds.width}px`)
            el.style.setProperty('--sizebox-height', `${bounds.height}px`)

            setSize({ width: bounds.width, height: bounds.height })
        }
    }, [])

    useLayoutEffect(() => {
        applySize(target.current)
    }, [applySize, target])

    useResizeObserver(target, (entry) => {
        applySize(entry.target as HTMLDivElement)
    })

    return size
}
